import { Navigate, useParams } from 'react-router-dom'

import { Loader2 } from 'lucide-react'
import { ROUTES } from '@/config/routes'
import { useCurrentUser, usePermissions } from '@/features/auth/state/auth'
import { useFetchClientByCnpjCpfTime } from '@/features/client/services/useFetchClientByCnpjCpfTIme'
import { useFetchClientesProducts } from '@/features/products/services/useFetchClientesProducts'

type Props = {
  children: JSX.Element
  permissionType: 'clients' | 'tenants'
}

function decideAuthorization({
  user,
  permissions,
  client,
  cnpjcpf,
  permissionType,
  clientsProduct
}: {
  user: any
  permissions: any
  client?: any | null
  cnpjcpf?: string
  permissionType: 'clients' | 'tenants'
  clientsProduct: any[]
}): boolean {
  if (!permissions) return false

  if (permissionType === 'clients') {
    if (!client) return false

    if (user.typeUser === 'ADMIN') {
      if (user.typeCargo === 'CEO' || permissions.permission_geral) {
        return true
      }

      if (user.typeCargo === 'ANALISTA') {
        if (permissions.permission_product) {
          return true
        } else {
          const hasClientWithAnalyst = clientsProduct?.some((item: any) => {
            const sameClient = item?.cnpjcpf === cnpjcpf
            const sameAnalyst = item.produtos.some((item: any) => item.id_analista === user.id)
            return sameClient && sameAnalyst
          })

          if (hasClientWithAnalyst) {
            return true
          }
        }
      }
      console.log(`vishh`)
    } else if (user.typeUser === 'USUARIO') {
      if (user.typeCargo === 'CEO' && user.id_tenant === client.id_tenant) {
        return true
      }

      if (user.id_tenant === client.id_tenant && (user.id === client.id_responsavel || user.id === client.id_gerente)) {
        return true
      }
    }
    return false
  }

  // tenants
  if (user.typeUser === 'ADMIN' && (user.typeCargo === 'CEO' || permissions.gestor_geral)) return true
  return !!permissions.gestor_geral
}
const CenterLoader = () => (
  <div className="flex w-full h-full items-center justify-center">
    <Loader2 className="animate-spin" />
  </div>
)
const PermissionRoute = ({ children, permissionType }: Props) => {
  // === HOOKS: sempre no topo, sempre na mesma ordem ===
  const user = useCurrentUser()
  const permissions = usePermissions()
  const { cnpjcpf } = useParams()

  const needsClient = permissionType === 'clients' && !!cnpjcpf

  // Chamar SEMPRE o hook; controlar com enabled
  const {
    data: client,
    isPending: clientPending,
    isError: clientError,
    isSuccess: clientSuccess
  } = useFetchClientByCnpjCpfTime(cnpjcpf as string, {
    enabled: needsClient,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  })

  const {
    data: clientsProduct = [],
    isPending: clientsPending,
    isError: clientsError
  } = useFetchClientesProducts(user?.id, { enabled: !!user?.id })

  // Sem user -> login
  if (!user) return <Navigate to={ROUTES.SIGNIN.path} replace />

  // Esperando permissões? (ajuste aqui caso use um hook com estados)
  if (!permissions) return <CenterLoader />

  // Se sua decisão usa clientsByUser, espere também
  if (!!user?.id && clientsPending) return <CenterLoader />
  if (clientsError) return <Navigate to={ROUTES.DASHBOARD_HOME.path} replace />

  // Esperando client (quando necessário)?
  if (needsClient && clientPending) return <CenterLoader />

  // Client não encontrado (success com null) ou erro -> fallback
  if (needsClient && (clientError || (clientSuccess && !client))) {
    return <Navigate to={ROUTES.ALERTS.path} replace />
  }

  // Sem user -> login
  if (!user) return <Navigate to={ROUTES.SIGNIN.path} replace />

  // Esperando permissões?
  if (!permissions) {
    return <CenterLoader />
  }

  // Esperando client (quando necessário)?
  if (needsClient && clientPending) {
    return <CenterLoader />
  }

  // Client não encontrado (success com null) ou erro -> fallback
  if (needsClient && (clientError || (clientSuccess && !client))) {
    return <Navigate to={ROUTES.ALERTS.path} replace />
  }

  // Decisão final
  const ok = decideAuthorization({
    user,
    permissions,
    client,
    cnpjcpf,
    permissionType,
    clientsProduct: clientsProduct ?? []
  })
  if (!ok) {
    return <Navigate to={user.typeUser === 'ADMIN' ? ROUTES.ADMIN_HOME.path : ROUTES.DASHBOARD_HOME.path} replace />
  }

  return children
}

export default PermissionRoute
